import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { useClientTranslation } from '@/lib/core/i18n';
import { useApp } from '@/lib/core/providers/appProvider';
import { formatAmount, randomUuid } from '@/lib/core/utils';
import type { CartShopDto, CheckoutConfigDto, PaymentMethodDto } from '@/services/platform/api/model';
import {
  CheckoutPaymentMethodTypes,
  CheckoutPaymentStatuses,
  createPaymentIntent,
  getPayment,
  isPaymentStatus,
  isTerminalPaymentStatus,
} from '@/services/platform/data/checkout';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { formatCardBrand } from '@/components/shared/profile/payment/utils';
import { fieldForStripeError, type CardField } from '@/components/shared/payment/card-fields';
import { BookingSummaryPanel } from './BookingSummaryPanel';
import { ConsentBlock } from './ConsentBlock';
import { PaymentMethodList } from './PaymentMethodList';
import { indexCmsMethods, logoForCardBrand, METHOD_SLUGS, resolveLogos, serverOffers } from './methods';
import { NewCardPanel } from './panels/NewCardPanel';
import { MbWayPanel } from './panels/MbWayPanel';
import { WalletExpressCheckout, type StripeLoader } from './WalletExpressCheckout';
import { isNativeWalletPlatform, payWithNativeWallet, probeNativeWallet } from './nativeWallet';
import type { CheckoutFormState, CheckoutMethodOption, CheckoutPurchase, CheckoutSelection } from './types';

/** How often to ask the server whether the webhook has landed. */
const POLL_INTERVAL_MS = 2000;

/**
 * How long to keep polling before telling the customer it is still in progress.
 *
 * Cards settle in seconds. MB WAY waits on the customer opening their banking app and approving,
 * which the Stripe docs allow several minutes for — giving up early would strand a payment that is
 * about to succeed.
 */
const POLL_TIMEOUT_MS = { card: 90_000, mb_way: 300_000 } as const;

/** Reads that fail in a row before the screen stops waiting and says so. */
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

export type CheckoutOutcome =
  /**
   * Paid, and the purchase exists. `orderId` is the resulting order's public guid and `orderNumber`
   * its human-facing number, both straight off the polled payment — the server only reports
   * `Succeeded` once the order is closed (`PaymentStatuses.ForClient`), so a confirmation screen can
   * use them directly instead of re-reading the orders list and assuming the newest one.
   */
  | { kind: 'succeeded'; reservationId?: string | null; orderId?: string | null; orderNumber?: string | null }
  | { kind: 'failed'; message: string }
  | { kind: 'pending'; message: string }
  | { kind: 'refunded'; message: string }
  /**
   * The server re-priced the purchase before charging. `repricedCart` is present for the shop, whose
   * 409 carries the refreshed basket; a context that can show the customer what changed should claim
   * this outcome rather than let it fall through to the generic error phase.
   */
  | { kind: 'stale'; message: string; repricedCart?: CartShopDto }
  /** Money has already been taken for this purchase. Not a decline — do not offer another card. */
  | { kind: 'alreadyPaid'; message: string }
  /**
   * An event refused before charging: Full or fewer seats than the party. `availableSpots` is what
   * remains (`null` = unreadable) — an adapter with seats to offer can send the member back to
   * choose fewer guests instead of ending on an error.
   */
  | { kind: 'eventSoldOut'; message: string; availableSpots: number | null }
  /** The event's chosen date no longer exists in Salesforce — nothing charged; another date is needed. */
  | { kind: 'eventDateGone'; message: string };

interface CheckoutFormProps {
  purchase: CheckoutPurchase;
  config: CheckoutConfigDto;
  savedCards: PaymentMethodDto[];
  /** CMS-authored `paymentMethods` blocks, for labels and logos. */
  cmsPaymentMethods?: Parameters<typeof indexCmsMethods>[0];
  locale: string;
  termsHref?: string;
  selection?: CheckoutSelection;
  onSelectionChange: (selection: CheckoutSelection) => void;
  /** Raised when the screen should switch to the processing view. */
  onProcessingChange: (processing: boolean, subtitle?: string) => void;
  onOutcome: (outcome: CheckoutOutcome) => void;
  /** Owned by the parent so it survives the `<Elements>` remount when the method changes. */
  formState: CheckoutFormState;
  onFormStateChange: (patch: Partial<CheckoutFormState>) => void;
  /**
   * A payment already in flight for this booking, recovered from `payments/active`. Polling resumes
   * immediately instead of creating a second intent — only one can exist per purchase.
   */
  resumePaymentId?: string;
  /**
   * Raised as soon as an intent exists, before it is confirmed. For contexts whose
   * `contextReference` does not survive a successful payment — the shop's cart *becomes* the order —
   * this id is the only way to find the payment again after a redirect, since `payments/active` no
   * longer lists it once it has settled.
   */
  onIntentCreated?: (paymentId: string) => void;
  /** Reused by the wallet's isolated deferred-mode Elements provider. */
  stripePromise: StripeLoader;
}

/**
 * The checkout body. Lives inside `<Elements>` so it can call `useStripe`/`useElements`.
 *
 * Owns everything the customer types plus the confirm-and-poll driver. The three confirm paths
 * differ only in how a PaymentMethod is produced; every one of them then polls
 * `GET /me/checkout/payments/{id}` the same way, because Stripe's webhook — not the confirm call —
 * is what completes a purchase.
 */
export function CheckoutForm({
  purchase,
  config,
  savedCards,
  cmsPaymentMethods,
  locale,
  termsHref,
  selection,
  onSelectionChange,
  onProcessingChange,
  onOutcome,
  resumePaymentId,
  formState,
  onFormStateChange,
  onIntentCreated,
  stripePromise,
}: CheckoutFormProps) {
  const t = useClientTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useApp();

  const {
    termsAccepted,
    marketingOptIn,
    holderName,
    saveForFuture,
    cardFields,
    cardErrors,
    submitAttempted,
  } = formState;

  // MB WAY's Payment Element is configured to skip these so it renders the phone field alone, as the
  // design draws it. That opt-out obliges us to supply them at confirm time, so the two have to be
  // derived from the same source — see `MbWayPanel`.
  const billingName = user?.name?.trim() || undefined;
  const billingEmail = user?.email?.trim() || undefined;

  const isPayingRef = useRef(false);
  const [walletAvailable, setWalletAvailable] = useState(false);

  // Read through a ref so the callback below keeps a stable identity: it is a prop of the Express
  // Checkout wrapper, and churning it on every selection change would re-register Stripe's element
  // listeners for no reason.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  /**
   * Stripe's verdict on whether a wallet can be offered.
   *
   * Losing availability has to move the selection, not just hide the row. The footer swaps the
   * generic CTA out whenever the wallet is selected, and the Express Checkout Element draws nothing
   * when it has no methods to show — so a selection still pointing at a row that has left the list
   * leaves the customer on a checkout with no pay button at all. The wallet row only ever exists
   * alongside the card rows (`cardOffered`), so falling back to a card is always possible.
   */
  const handleWalletAvailabilityChange = useCallback(
    (available: boolean) => {
      setWalletAvailable(available);
      if (available || selectionRef.current?.kind !== 'wallet') return;

      const fallbackCard = savedCards.find(card => card.id)?.id;
      onSelectionChange(
        fallbackCard ? { kind: 'savedCard', paymentMethodId: fallbackCard } : { kind: 'newCard' }
      );
    },
    [onSelectionChange, savedCards]
  );

  /**
   * On the Capacitor builds the row's availability comes from asking the device, not from the web
   * element — which is never mounted there, because Stripe.js cannot offer a wallet inside a webview.
   * Keyed on the publishable key so a re-probe only happens if the account actually changes.
   */
  const nativeWallet = isNativeWalletPlatform();
  const publishableKey = config.publishableKey;
  useEffect(() => {
    if (!nativeWallet || !publishableKey) return;
    let cancelled = false;

    void probeNativeWallet(publishableKey).then(available => {
      if (!cancelled) handleWalletAvailabilityChange(available);
    });

    return () => {
      cancelled = true;
    };
  }, [nativeWallet, publishableKey, handleWalletAvailabilityChange]);

  /**
   * True while this instance is mounted, so nothing reports an outcome after the customer has left.
   *
   * The poll loop outlives this component — `handlePay` is async and keeps running once the screen
   * is gone — and its outcome navigates. Without this, leaving the processing overlay and then
   * having the payment settle would yank the customer out of wherever they had moved on to.
   *
   * Armed inside the effect body, not only via `useRef(true)`'s initial value: React's Strict Mode
   * (dev only) mounts, unmounts and remounts every component once to surface exactly this class of
   * bug. The "remount" reuses the same ref object rather than creating a fresh one, so a ref that is
   * only ever cleared by the cleanup — never re-armed by the effect itself — comes back from that
   * dance permanently stuck at `false`, correct-looking in production but broken in every dev
   * session. This is what silently swallowed every outcome after the Stripe confirm.
   */
  const isActiveRef = useRef(true);
  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const currency = purchase.currencyCode ?? 'EUR';
  const totalMajor = purchase.amountMajor;
  // Stripe's `paymentRequest` and deferred Elements both take minor units; the API and
  // `resolveBookingScope` both work in major units. Convert once, here.
  const totalMinor = Math.round(totalMajor * 100);
  const formattedTotal = formatAmount(totalMajor, currency, locale);

  function setCardError(field: CardField, message?: string) {
    if (cardErrors[field] === message) return;
    const next = { ...cardErrors };
    if (message) next[field] = message;
    else delete next[field];
    onFormStateChange({ cardErrors: next });
  }

  /**
   * Polls until the payment reaches a state it cannot leave.
   *
   * Deliberately not a `setInterval`: each request is awaited before the next is scheduled, so a
   * slow response cannot stack overlapping polls.
   */
  const pollUntilTerminal = useCallback(
    async (paymentId: string, timeoutMs: number): Promise<CheckoutOutcome> => {
      const deadline = Date.now() + timeoutMs;
      let consecutiveFailures = 0;

      for (;;) {
        // Stop the moment the screen is gone: the webhook still completes the payment server-side,
        // so there is nothing to watch for and nobody to report to.
        if (!isActiveRef.current) return { kind: 'pending', message: '' };

        const payment = await getPayment(paymentId);

        // A read that fails is not the same as a payment that has not finished, but both look like
        // an unchanging poll from the outside. Give up after a few in a row rather than spinning on
        // a 404 or an expired session for the whole timeout.
        if (!payment) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            console.warn(
              `pollUntilTerminal: ${consecutiveFailures} consecutive failed reads for ${paymentId}.`
            );
            return { kind: 'failed', message: t('App.Checkout.PaymentUnavailable') };
          }
        } else {
          consecutiveFailures = 0;
          // The status is the single most useful thing to see when a payment appears stuck: it says
          // whether the webhook has landed at all, and separates a backend problem from a UI one.
          console.info(`pollUntilTerminal: payment ${paymentId} status=${payment.status}`);
        }

        if (isTerminalPaymentStatus(payment?.status)) {
          if (isPaymentStatus(payment?.status, CheckoutPaymentStatuses.Succeeded)) {
            return {
              kind: 'succeeded',
              reservationId: payment?.reservationId,
              orderId: payment?.orderId,
              orderNumber: payment?.orderNumber,
            };
          }
          // The charge went through and was then reversed server-side — the booking could not be
          // completed (e.g. sold out between charge and registration), not a declined card. Must be
          // checked before the generic failed case: it is a distinct terminal status, not a variant
          // of it, and "declined, try another method" would be actively wrong here.
          if (isPaymentStatus(payment?.status, CheckoutPaymentStatuses.Refunded)) {
            return { kind: 'refunded', message: t('App.Checkout.PaymentRefunded') };
          }
          return {
            kind: 'failed',
            message: payment?.failureCode
              ? t('App.Checkout.PaymentDeclined')
              : t('App.Checkout.PaymentFailed'),
          };
        }

        if (Date.now() >= deadline) {
          return { kind: 'pending', message: t('App.Checkout.PaymentTimedOut') };
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    },
    [t]
  );

  // Recover a payment left in flight — with MB WAY the customer leaves the app to approve and can
  // come back to a cold start. Runs once; `resumePaymentId` is resolved before this mounts.
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (!resumePaymentId || hasResumedRef.current) return;
    hasResumedRef.current = true;

    onProcessingChange(true, t('App.Checkout.ApprovePaymentOnPhone'));
    void pollUntilTerminal(resumePaymentId, POLL_TIMEOUT_MS.mb_way).then(onOutcome);
  }, [resumePaymentId, onProcessingChange, onOutcome, pollUntilTerminal, t]);

  const cmsIndex = useMemo(() => indexCmsMethods(cmsPaymentMethods), [cmsPaymentMethods]);

  /**
   * Creates the intent, mapping every failure onto copy the customer can act on.
   *
   * A fresh idempotency key per call, because each call *is* one attempt: reusing the key across
   * attempts would make a retry after a decline replay the cached failure instead of trying again.
   * Double-taps are held off by `isPayingRef`, and a create whose response was lost is recovered by
   * `payments/active` on the next mount rather than by key reuse — the server allows only one
   * payment in flight per purchase, so a duplicate cannot be created behind our back.
   */
  const startIntent = useCallback(
    async (paymentMethodType: string, savedPaymentMethodId?: string) => {
      const result = await createPaymentIntent(
        {
          context: purchase.context,
          contextReference: purchase.contextReference,
          paymentMethodType,
          savedPaymentMethodId: savedPaymentMethodId ?? null,
          eventBooking: purchase.eventBooking,
        },
        randomUuid()
      );

      if (result.ok) {
        // Announced before the confirm, not after the poll: with a redirect-based method the next
        // thing that happens may be the page unloading, and a context whose reference does not
        // survive the round trip (the shop's cart becomes an order) has no other way back to this
        // payment. Everyone else ignores it.
        if (result.intent.paymentId) onIntentCreated?.(result.intent.paymentId);
        return result.intent;
      }

      // Reported as its own kind rather than flattened into `failed`, so an adapter can act on it —
      // a re-priced basket and an already-paid one both have somewhere better to go than an error
      // screen. Unclaimed, they land on the same error phase they always did.
      if (result.failure === 'stale') {
        onOutcome({
          kind: 'stale',
          message: t('App.Checkout.PriceChanged'),
          repricedCart: result.repricedCart,
        });
        return undefined;
      }
      if (result.failure === 'alreadyPaid') {
        onOutcome({ kind: 'alreadyPaid', message: t('App.Checkout.AlreadyPaid') });
        return undefined;
      }
      if (result.failure === 'eventSoldOut') {
        onOutcome({
          kind: 'eventSoldOut',
          message: t('App.Checkout.EventSoldOut'),
          availableSpots: result.availableSpots ?? null,
        });
        return undefined;
      }
      if (result.failure === 'occurrenceNotFound') {
        onOutcome({ kind: 'eventDateGone', message: t('App.Checkout.EventDateGone') });
        return undefined;
      }

      const message =
        result.failure === 'eventNotForSale'
          ? t('App.Checkout.EventNotForSale')
          : result.failure === 'eventAvailabilityUnavailable'
            ? t('App.Checkout.EventAvailabilityUnavailable')
            : result.failure === 'rejected' || result.failure === 'notFound'
              ? t('App.Checkout.BookingRejected')
              : t('App.Checkout.PaymentUnavailable');
      onOutcome({ kind: 'failed', message });
      return undefined;
    },
    [purchase.context, purchase.contextReference, purchase.eventBooking, onIntentCreated, onOutcome, t]
  );

  /**
   * Deferred Express Checkout confirmation. Stripe opens the wallet from its branded button and
   * invokes this only after authorisation, so selecting the row never creates an abandoned intent.
   */
  const handleWalletConfirm = useCallback(
    async (walletStripe: Stripe, walletElements: StripeElements) => {
      if (!termsAccepted || isPayingRef.current) return;
      isPayingRef.current = true;
      onProcessingChange(true);

      let settled = false;
      const settle = (outcome: CheckoutOutcome) => {
        settled = true;
        if (isActiveRef.current) onOutcome(outcome);
      };
      const abort = (reason: string) => {
        console.warn(`handleWalletConfirm: aborted before confirming — ${reason}`);
        settle({ kind: 'failed', message: t('App.Checkout.PaymentUnavailable') });
      };

      try {
        const submitted = await walletElements.submit();
        if (submitted.error) {
          settle({
            kind: 'failed',
            message: submitted.error.message ?? t('App.Checkout.PaymentFailed'),
          });
          return;
        }

        const intent = await startIntent(CheckoutPaymentMethodTypes.Card);
        if (!intent) {
          // `startIntent` already raised the precise outcome.
          settled = true;
          return;
        }

        if (!intent.clientSecret || !intent.paymentId) {
          abort(
            `intent response missing fields (paymentId=${intent.paymentId}, ` +
              `clientSecret=${!!intent.clientSecret}, status=${intent.status})`
          );
          return;
        }

        const confirmed = await walletStripe.confirmPayment({
          elements: walletElements,
          clientSecret: intent.clientSecret,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (confirmed.error) {
          settle({
            kind: 'failed',
            message: confirmed.error.message ?? t('App.Checkout.PaymentFailed'),
          });
          return;
        }

        settle(await pollUntilTerminal(intent.paymentId, POLL_TIMEOUT_MS.card));
      } catch (error) {
        console.warn('handleWalletConfirm: threw:', error);
        settle({ kind: 'failed', message: t('App.Checkout.PaymentFailed') });
      } finally {
        isPayingRef.current = false;
        if (!settled) onProcessingChange(false);
      }
    },
    [onOutcome, onProcessingChange, pollUntilTerminal, startIntent, t, termsAccepted]
  );

  /**
   * The same payment on the native path, where the order is reversed: the plugin has no deferred mode,
   * so the intent must exist before the sheet can open. That is what the server contract always
   * wanted, and an abandoned sheet is safe — see `payWithNativeWallet`.
   */
  const handleNativeWalletPay = useCallback(async () => {
    if (isPayingRef.current) return;
    isPayingRef.current = true;
    onProcessingChange(true);

    let settled = false;
    const settle = (outcome: CheckoutOutcome) => {
      settled = true;
      if (isActiveRef.current) onOutcome(outcome);
    };

    try {
      const intent = await startIntent(CheckoutPaymentMethodTypes.Card);
      if (!intent) {
        // `startIntent` already raised the precise outcome.
        settled = true;
        return;
      }
      if (!intent.clientSecret || !intent.paymentId) {
        console.warn(
          `handleNativeWalletPay: intent response missing fields (paymentId=${intent.paymentId}, ` +
            `clientSecret=${!!intent.clientSecret})`
        );
        settle({ kind: 'failed', message: t('App.Checkout.PaymentUnavailable') });
        return;
      }

      // Apple Pay draws its own sheet from these rather than from the intent, so they have to be the
      // same figures the screen behind it is showing. Google Pay ignores them. The line-item label
      // is left to default to the merchant name, which is what an Apple Pay sheet is expected to
      // name — the itemised breakdown is already on the screen underneath.
      const result = await payWithNativeWallet({
        clientSecret: intent.clientSecret,
        amountMajor: totalMajor,
        currencyCode: currency,
      });

      // Dismissing the sheet is not a decline, and must not read like one: leaving `settled` false
      // lets the `finally` release the processing view and drop the customer back on the method list.
      if (result === 'canceled') return;
      if (result === 'failed') {
        settle({ kind: 'failed', message: t('App.Checkout.PaymentFailed') });
        return;
      }

      settle(await pollUntilTerminal(intent.paymentId, POLL_TIMEOUT_MS.card));
    } catch (error) {
      console.warn('handleNativeWalletPay: threw:', error);
      settle({ kind: 'failed', message: t('App.Checkout.PaymentFailed') });
    } finally {
      isPayingRef.current = false;
      if (!settled) onProcessingChange(false);
    }
  }, [currency, onOutcome, onProcessingChange, pollUntilTerminal, startIntent, t, totalMajor]);

  const walletLabel = t('App.Checkout.Wallets');
  const cardOffered = serverOffers(config.paymentMethodTypes, CheckoutPaymentMethodTypes.Card);

  /** The rows, reconciling saved cards, what the server offers, and CMS content. */
  const options = useMemo<CheckoutMethodOption[]>(() => {
    const rows: CheckoutMethodOption[] = [];

    if (cardOffered) {
      for (const card of savedCards) {
        if (!card.id) continue;
        rows.push({
          selection: { kind: 'savedCard', paymentMethodId: card.id },
          label: t('App.Checkout.PayWithCard', {
            brand: formatCardBrand(card.brand),
            last4: card.last4 ?? '',
          }),
          logos: logoForCardBrand(card.brand),
          savedCard: card,
        });
      }

      rows.push({
        selection: { kind: 'newCard' },
        label: t('App.Checkout.CreditCard'),
        logos: resolveLogos(METHOD_SLUGS.card, cmsIndex),
      });

      // Hidden outright when the device cannot pay — a row that does nothing on tap is worse than
      // no row, and `canMakePayment` is the only reliable signal.
      if (walletAvailable) {
        rows.push({
          selection: { kind: 'wallet' },
          label: walletLabel,
          logos: resolveLogos(METHOD_SLUGS.wallet, cmsIndex),
        });
      }
    }

    if (serverOffers(config.paymentMethodTypes, CheckoutPaymentMethodTypes.MbWay)) {
      rows.push({
        selection: { kind: 'mbWay' },
        label: t('App.Checkout.MbWay'),
        logos: resolveLogos(METHOD_SLUGS.mbWay, cmsIndex),
      });
    }

    return rows;
  }, [cardOffered, cmsIndex, config.paymentMethodTypes, savedCards, t, walletAvailable, walletLabel]);

  const cardComplete =
    cardFields.complete.number &&
    cardFields.complete.expiry &&
    cardFields.complete.cvc &&
    !!holderName.trim();

  /**
   * Whether pressing pay can do anything at all.
   *
   * Terms is a hard gate. The card row stays tappable once the customer has engaged, so a press can
   * point at the unfinished field rather than dead-ending on a greyed-out button — the same rule
   * `profile/payment/add.tsx` follows.
   */
  const cardEngaged =
    cardFields.touched.number || cardFields.touched.expiry || cardFields.touched.cvc || !!holderName.trim();
  const canPay = !!selection && termsAccepted && (selection.kind !== 'newCard' || cardEngaged);

  async function handlePay() {
    if (!selection || !stripe || isPayingRef.current) return;
    onFormStateChange({ submitAttempted: true });

    // Express Checkout owns the wallet button and never calls this generic CTA.
    if (selection.kind === 'wallet') return;

    if (selection.kind === 'newCard' && !cardComplete) return;

    isPayingRef.current = true;
    onProcessingChange(true);

    // Every exit from here has to end in an outcome. The screen is already showing the processing
    // view, so a bare `return` leaves a spinner with nothing behind it and no way out — which is
    // exactly what a missing client secret used to produce.
    let settled = false;
    const settle = (outcome: CheckoutOutcome) => {
      settled = true;
      // Dropped rather than reported once the customer has left — see `isActiveRef`.
      if (isActiveRef.current) onOutcome(outcome);
    };
    /** Bails with a visible message and says in the console which condition tripped. */
    const abort = (reason: string) => {
      console.warn(`handlePay: aborted before confirming — ${reason}`);
      settle({ kind: 'failed', message: t('App.Checkout.PaymentUnavailable') });
    };

    try {
      if (selection.kind === 'mbWay') {
        if (!elements) return abort('Stripe Elements is not mounted');
        // Deferred mode: Elements has to validate and tokenise before an intent exists.
        const submitted = await elements.submit();
        if (submitted.error) {
          settle({
            kind: 'failed',
            message: submitted.error.message ?? t('App.Checkout.PaymentFailed'),
          });
          return;
        }

        const intent = await startIntent(CheckoutPaymentMethodTypes.MbWay);
        // `startIntent` already reported its own failure, so only a malformed success lands here.
        if (!intent) return;
        if (!intent.clientSecret || !intent.paymentId) {
          return abort(
            `intent response missing fields (paymentId=${intent.paymentId}, clientSecret=${!!intent.clientSecret})`
          );
        }

        // `return_url` is mandatory the moment Stripe decides a redirect is needed — which it does
        // for MB WAY's hosted authorisation page in test mode. Without it `confirmPayment` rejects
        // outright instead of paying. Returning to this same URL is safe: the booking is recovered
        // from session storage and `payments/active` picks the payment back up mid-flight.
        const confirmed = await stripe.confirmPayment({
          elements,
          clientSecret: intent.clientSecret,
          confirmParams: {
            return_url: window.location.href,
            // Mandatory counterpart to the `fields` opt-out in `MbWayPanel`: whatever the element
            // was told not to collect has to arrive here instead. The phone itself is collected by
            // the element and must not be passed.
            payment_method_data: {
              billing_details: { name: billingName, email: billingEmail },
            },
          },
          redirect: 'if_required',
        });

        if (confirmed.error) {
          settle({
            kind: 'failed',
            message: confirmed.error.message ?? t('App.Checkout.PaymentFailed'),
          });
          return;
        }

        // MB WAY parks in `RequiresAction` until the customer approves on their phone, so the
        // processing screen has to say what it is waiting for.
        onProcessingChange(true, t('App.Checkout.ApprovePaymentOnPhone'));
        settle(await pollUntilTerminal(intent.paymentId, POLL_TIMEOUT_MS.mb_way));
        return;
      }

      const savedId = selection.kind === 'savedCard' ? selection.paymentMethodId : undefined;
      const intent = await startIntent(CheckoutPaymentMethodTypes.Card, savedId);
      if (!intent) return;
      if (!intent.clientSecret || !intent.paymentId) {
        return abort(
          `intent response missing fields (paymentId=${intent.paymentId}, clientSecret=${!!intent.clientSecret})`
        );
      }
      if (selection.kind === 'newCard' && !elements?.getElement(CardNumberElement)) {
        return abort('the card number element is not mounted');
      }

      const confirmed =
        selection.kind === 'savedCard'
          ? // The id has to be handed to Stripe here. The API takes `savedPaymentMethodId` only to
            // check the card belongs to this customer (`ValidateMethodAsync`) — it never attaches it
            // to the PaymentIntent, which is created with no payment method at all. Confirming
            // without one is what Stripe rejects with a 400.
            await stripe.confirmCardPayment(intent.clientSecret, {
              payment_method: selection.paymentMethodId,
            })
          : await stripe.confirmCardPayment(intent.clientSecret, {
              payment_method: {
                // Passed by component reference, matching `profile/payment/add.tsx` — the string
                // form resolves against a different registry and returns null here.
                card: elements!.getElement(CardNumberElement)!,
                billing_details: { name: holderName.trim() },
              },
              // Stripe attaches the card to the customer for reuse only when asked.
              setup_future_usage: saveForFuture ? 'off_session' : undefined,
            });

      if (confirmed.error) {
        const field = fieldForStripeError(confirmed.error.param, confirmed.error.code);
        const message = confirmed.error.message ?? t('App.Checkout.PaymentFailed');
        // A field-attributable decline goes back to the form so the customer can correct it,
        // rather than to the full-screen error which would discard the whole card.
        if (field && selection.kind === 'newCard') {
          setCardError(field, message);
          settled = true;
          onProcessingChange(false);
          return;
        }
        settle({ kind: 'failed', message });
        return;
      }

      // Saving the card is `setup_future_usage` above and nothing more. Marking it *preferred* is a
      // separate choice the customer makes on their profile — "save for future transactions" does
      // not say "make this my default", and silently promoting it would reorder their cards.
      settle(await pollUntilTerminal(intent.paymentId, POLL_TIMEOUT_MS.card));
    } catch (error) {
      // A throw here (a Stripe.js rejection, a network drop mid-confirm) would otherwise leave the
      // processing view up with nothing running behind it.
      console.warn('handlePay: threw before settling:', error);
      settle({ kind: 'failed', message: t('App.Checkout.PaymentFailed') });
    } finally {
      isPayingRef.current = false;
      // Last line of defence: no path may leave the screen showing a spinner forever.
      if (!settled) {
        console.warn('handlePay: returned without an outcome — releasing the processing view.');
        onProcessingChange(false);
      }
    }
  }

  function renderPanel(option: CheckoutMethodOption) {
    const key = option.selection.kind === 'savedCard' ? 'saved' : option.selection.kind;
    const consent = (
      <ConsentBlock
        idPrefix={key}
        termsAccepted={termsAccepted}
        onTermsChange={accepted => onFormStateChange({ termsAccepted: accepted })}
        marketingOptIn={marketingOptIn}
        onMarketingChange={optIn => onFormStateChange({ marketingOptIn: optIn })}
        termsHref={termsHref}
      />
    );

    switch (option.selection.kind) {
      case 'newCard':
        return (
          <div className="flex flex-col gap-4">
            <NewCardPanel
              holderName={holderName}
              onHolderNameChange={name => onFormStateChange({ holderName: name })}
              saveForFuture={saveForFuture}
              onSaveForFutureChange={save => onFormStateChange({ saveForFuture: save })}
              fieldState={cardFields}
              onFieldStateChange={next => onFormStateChange({ cardFields: next })}
              errors={cardErrors}
              onErrorChange={setCardError}
              submitAttempted={submitAttempted}
            />
            {consent}
          </div>
        );
      case 'mbWay':
        return (
          <div className="flex flex-col gap-4">
            <MbWayPanel hideName={!!billingName} hideEmail={!!billingEmail} />
            {consent}
          </div>
        );
      default:
        return consent;
    }
  }

  if (options.length === 0) {
    return (
      <p
        role="alert"
        className="font-pp-neue-montreal text-jncquoi-neutral-800 px-4 py-10 text-center text-[14px] tracking-[0.5px]"
      >
        {t('App.Checkout.NoMethodsAvailable')}
      </p>
    );
  }

  return (
    <>
      {/*
        The scrolling region. `min-h-0` is load-bearing: a flex item's default `min-height: auto`
        refuses to shrink below its content, so without it this grows to fit the expanded card panel,
        pushes the footer off-screen and hands the scroll back to the page.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Accordion type="single" collapsible className="bg-jncquoi-neutral-50">
          <AccordionItem value="summary" className="border-t-0">
            <AccordionTrigger className="px-4">
              {purchase.summaryTitle ?? t('App.Checkout.ViewBookingSummary')}
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              {purchase.summaryContent ?? (
                <BookingSummaryPanel
                  rows={purchase.summaryRows ?? []}
                  totalMajor={purchase.amountMajor}
                  currencyCode={purchase.currencyCode}
                  locale={locale}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="flex flex-col gap-5 px-4 pt-6 pb-8">
          <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-[24px] font-semibold">
            {t('App.Checkout.SelectPaymentMethod')}
          </h2>
          <PaymentMethodList
            options={options}
            selected={selection}
            onSelect={onSelectionChange}
            renderPanel={renderPanel}
          />
        </div>
      </div>

      {/*
        In-flow footer rather than `fixed`: it is a `shrink-0` sibling of the scrolling region above,
        so it stays visible without the scroll area needing padding to hide behind it.

        The button sits `42px + safe-area` above the screen edge, the same as the booking flows' own
        sticky bars (`mews/details.tsx`, `mews/rate.tsx`) that lead here. `CheckoutScreen`'s container
        already ends `max(1rem, safe-area)` above the edge, so that band is subtracted here rather
        than counted twice — adding the inset again left the CTA 22–40px higher than the steps before.
      */}
      <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 shrink-0 border-t-[0.5px] px-4 pt-4 pb-[calc(42px+env(safe-area-inset-bottom)-max(1rem,env(safe-area-inset-bottom)))]">
        <div className="flex items-center justify-between gap-4">
          {cardOffered && !nativeWallet && (
            <div
              className={
                selection?.kind === 'wallet'
                  ? `min-w-0 flex-1 ${termsAccepted ? '' : 'pointer-events-none opacity-50'}`
                  : 'pointer-events-none fixed top-0 -left-[10000px] w-80 opacity-0'
              }
              aria-disabled={selection?.kind === 'wallet' ? !termsAccepted : undefined}
              aria-hidden={selection?.kind !== 'wallet'}
              inert={selection?.kind !== 'wallet' || !termsAccepted ? true : undefined}
            >
              <WalletExpressCheckout
                stripe={stripePromise}
                amountMinor={totalMinor}
                currency={currency}
                disabled={!termsAccepted || selection?.kind !== 'wallet'}
                onAvailabilityChange={handleWalletAvailabilityChange}
                onConfirm={handleWalletConfirm}
              />
            </div>
          )}
          {/*
            The native path draws its own button, because the plugin draws none. White with an outline
            rather than black: the mark in `google-pay.svg` sets "Pay" in #3C4043, which disappears on
            a dark ground — and white-with-outline is an official Google Pay variant, so this stays
            within their brand guidelines. 48px matches the height the web element was configured with.
          */}
          {nativeWallet && selection?.kind === 'wallet' && (
            <button
              type="button"
              disabled={!termsAccepted}
              onClick={() => void handleNativeWalletPay()}
              aria-label={walletLabel}
              className="border-jncquoi-neutral-300 flex h-12 min-w-0 flex-1 items-center justify-center border bg-white disabled:opacity-50"
            >
              <img src="/images/payments/google-pay.svg" alt="" aria-hidden="true" className="h-5 w-auto" />
            </button>
          )}
          {selection?.kind !== 'wallet' && (
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              className="max-w-68 flex-1"
              disabled={!canPay}
              onClick={handlePay}
            >
              {t('App.Checkout.GoToPayment')}
            </Button>
          )}
          <div className="flex flex-col items-end">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold">
              {formattedTotal}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 mt-1 text-[11px] leading-none">
              {t('App.Shop.TaxesIncludedNote')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
